<template>
  <MDBNavbarItem class="menu-item-border p-relative">
    <a
      href="#"
      class="nav-link px-lg-3 px-1 px-sm-2 py-0"
      :title="$t('pageName.favoriteProducts')"
      :class="{ 'favorites-preview-trigger--disabled': favoritePreviewBusy }"
      @click.prevent="toggleFavoritesPreview"
    >
      <MDBIcon icon="heart" size="2x" iconStyle="fas" class="text-danger font-22" />
      <MDBBadge v-if="me && me.favorites.length > 0" color="danger" pill notification>
        {{ me.favorites.length }}
      </MDBBadge>
    </a>

    <div
      v-if="favoritesPreviewOpen"
      class="favorites-preview-backdrop"
      :class="{ 'favorites-preview-trigger--disabled': favoritePreviewBusy }"
      @click="closeFavoritesPreview"
    ></div>

    <div v-if="favoritesPreviewOpen" class="favorites-preview-wrap">
      <h5 class="fw-bold mb-3">{{ $t("global.nav.favoriteProducts") }}</h5>
      <p v-if="favoriteLoading" class="text-muted mb-3">Loading...</p>
      <template v-else>
        <ul v-if="favoriteProducts.length > 0" class="favorites-preview-list mb-3">
          <li
            v-for="(item, i) in favoriteProducts"
            :key="`favorite_preview_${i}`"
            class="favorites-preview-item"
          >
            <router-link
              :to="{ name: 'productDetails', params: { id: item.product?.id } }"
              class="favorites-preview-item__link"
              @click="favoritesPreviewOpen = false"
            >
              {{ getFavoriteProductName(item) }}
            </router-link>
            <MDBBtn
              color="link"
              size="sm"
              class="favorites-preview-item__remove text-danger p-0"
              :disabled="favoriteRemoveBusy[itemKey(item)]"
              @click="removeFavorite(item)"
            >
              {{ $t("global.buttons.delete") }}
            </MDBBtn>
          </li>
        </ul>
        <p v-else class="text-muted mb-3">{{ $t("shop.order.anyOrder") }}</p>
      </template>

      <MDBBtn color="primary" size="sm" class="w-100" @click="goToFavoriteProducts">
        {{ $t("global.nav.favoriteProducts") }}
      </MDBBtn>
    </div>
  </MDBNavbarItem>
</template>

<script setup>
import { MDBNavbarItem, MDBBadge, MDBIcon, MDBBtn } from "mdb-vue-ui-kit";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { ProductService } from "@/services/product.service";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const me = computed(() => store.getters.getMe);
const storedLocale = computed(() => store.getters.getCurrentLocale);

const favoritesPreviewOpen = ref(false);
const favoriteLoading = ref(false);
const favoritePreviewBusy = ref(false);
const favoriteProducts = ref([]);
const favoriteRemoveBusy = ref({});
const lastFavoriteFetchAt = ref(0);
const FAVORITE_PREVIEW_FETCH_COOLDOWN_MS = 2500;

const itemKey = (item) =>
  `${item?.product_id ?? item?.product?.plainId ?? item?.product?.id}_${
    item?.product_variant_id ?? item?.productVariant?.id ?? "none"
  }`;

const closeFavoritesPreview = () => {
  if (favoritePreviewBusy.value) {
    return;
  }
  favoritesPreviewOpen.value = false;
};

const shouldFetchFavorites = () => {
  if (!lastFavoriteFetchAt.value) {
    return true;
  }
  return Date.now() - lastFavoriteFetchAt.value > FAVORITE_PREVIEW_FETCH_COOLDOWN_MS;
};

const loadFavoriteProducts = async () => {
  const userId = me.value?.plainId;
  if (!userId) {
    favoriteProducts.value = [];
    return;
  }

  const response = await ProductService.userFavoriteProducts(userId);
  favoriteProducts.value = Array.isArray(response?.data?.data) ? response.data.data : [];
};

const toggleFavoritesPreview = async () => {
  if (favoritePreviewBusy.value) {
    return;
  }

  if (favoritesPreviewOpen.value) {
    closeFavoritesPreview();
    return;
  }

  favoritesPreviewOpen.value = true;
  const needsFetch = shouldFetchFavorites();
  favoriteLoading.value = needsFetch;
  if (!needsFetch) {
    return;
  }

  favoritePreviewBusy.value = true;
  try {
    await loadFavoriteProducts();
    lastFavoriteFetchAt.value = Date.now();
  } finally {
    favoriteLoading.value = false;
    favoritePreviewBusy.value = false;
  }
};

const getFavoriteProductName = (item) => {
  const name = item?.product?.name;
  if (typeof name === "string") {
    return name;
  }
  if (name && typeof name === "object") {
    return (
      name[storedLocale.value] ||
      Object.values(name).find((value) => Boolean(value)) ||
      ""
    );
  }
  return "";
};

const removeFavorite = async (item) => {
  const key = itemKey(item);
  if (favoriteRemoveBusy.value[key]) {
    return;
  }

  favoriteRemoveBusy.value = {
    ...favoriteRemoveBusy.value,
    [key]: true,
  };

  try {
    const data = {
      user_id: me.value?.plainId,
      product_id: item?.product_id ?? item?.product?.plainId ?? item?.product?.id,
      product_variant_id:
        item?.product_variant_id ?? item?.productVariant?.id ?? null,
    };

    const response = await ProductService.saveDeleteFavorite(data);
    if (!response || response.status >= 400) {
      const message =
        response?.data?.message || response?.data?.error || t("toast.waitMoment");
      toast(message, {
        autoClose: 8000,
        type: "error",
        theme: "colored",
        transition: toast.TRANSITIONS.SLIDE,
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return;
    }

    favoriteProducts.value = favoriteProducts.value.filter(
      (favoriteItem) => itemKey(favoriteItem) !== key,
    );
    await store.dispatch("me");
    toast(t("toast.favoriteRemoved"), {
      autoClose: 5000,
      type: "success",
      theme: "colored",
      transition: toast.TRANSITIONS.SLIDE,
      position: toast.POSITION.BOTTOM_RIGHT,
    });
  } finally {
    favoriteRemoveBusy.value = {
      ...favoriteRemoveBusy.value,
      [key]: false,
    };
  }
};

const goToFavoriteProducts = () => {
  favoritesPreviewOpen.value = false;
  router.push({ name: "favoriteProducts" });
};
</script>

<style scoped lang="scss">
.favorites-preview-backdrop {
  position: fixed;
  inset: 0;
  background: transparent;
  z-index: 998;
}

.favorites-preview-wrap {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 320px;
  max-height: 380px;
  overflow: auto;
  background: white;
  border: 1px solid #e5e5e5;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  padding: 14px;
  z-index: 999;
}

.favorites-preview-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.favorites-preview-item {
  padding: 6px 0;
  border-bottom: 1px solid #f2f2f2;
}

.favorites-preview-item:last-child {
  border-bottom: 0;
}

.favorites-preview-item__link {
  color: #212529;
  text-decoration: none;
  display: block;
}

.favorites-preview-item__link:hover {
  text-decoration: underline;
}

.favorites-preview-item__remove {
  font-size: 12px;
}

.favorites-preview-trigger--disabled {
  pointer-events: none;
}
</style>
